import { useEffect, useId, useRef, type ReactNode } from 'react';
import { Icon } from './Icon.js';

/**
 * A gaveta da `DeskModal`: abre à direita, por cima da coluna do movimento e
 * de uma faixa do miolo — 30rem, a medida do protótipo, porque a foto precisa
 * de largura —, e deixa o resto da mesa, o formulário principalmente, à vista
 * e preenchível.
 *
 * ## Por que não é o `Sheet`
 *
 * O `Sheet` de `primitives/sheet.tsx` é um `Dialog` do Radix: modal, com véu
 * e foco preso. Aqui isso travaria justamente o formulário que a gaveta
 * existe para deixar livre — quem analisa preenche olhando a foto. Então ela
 * é um `<aside>` comum dentro do corpo da mesa, **sem véu e sem foco preso**.
 *
 * ## O Esc
 *
 * Quem fecha no Esc é a `DeskModal`, e não a gaveta: o primeiro Esc fecha a
 * gaveta, e só o segundo fecha o modal. Uma escuta de teclado aqui dentro
 * competiria com a do Radix, que fica no documento.
 *
 * ## No telefone
 *
 * Vira uma folha que sobe de baixo, com 72% da altura do modal — do lado, ela
 * cobriria a tela inteira e o formulário sumiria.
 *
 * Ao abrir, o foco vai para a gaveta, para quem usa teclado ou leitor de tela
 * saber que ela abriu. Trocar o conteúdo (outra foto, outro item) não move o
 * foco de novo.
 */
export function SideDrawer({
  eyebrow,
  title,
  onClose,
  closeLabel,
  actions,
  children,
}: {
  /** A linha pequena acima do título — "Do aviso #214", "Item 2 de 5". */
  eyebrow?: ReactNode;
  title: ReactNode;
  onClose: () => void;
  /** "Fechar o ocorrido" — o rótulo do X. */
  closeLabel: string;
  /** Botões ao lado do X — anterior e próximo item. */
  actions?: ReactNode;
  children: ReactNode;
}) {
  const titleId = useId();
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    ref.current?.focus();
  }, []);

  return (
    <aside
      ref={ref}
      tabIndex={-1}
      aria-labelledby={titleId}
      className="fixed inset-x-0 bottom-0 z-10 flex h-[72%] flex-col rounded-t-grande border-t border-borda bg-superficie shadow-flutuante outline-none lg:absolute lg:inset-y-0 lg:right-0 lg:left-auto lg:h-auto lg:w-[30rem] lg:max-w-[calc(100%-20rem)] lg:rounded-none lg:border-t-0 lg:border-l"
    >
      <div className="flex items-start justify-between gap-2.5 border-b border-borda px-4 pt-3.5 pb-3">
        <div className="min-w-0">
          {eyebrow && (
            <div className="text-[0.6875rem] font-semibold tracking-wide text-texto-tenue uppercase">
              {eyebrow}
            </div>
          )}
          <h3 id={titleId} className="mt-0.5 text-base font-semibold">
            {title}
          </h3>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {actions}
          <button
            type="button"
            aria-label={closeLabel}
            onClick={onClose}
            className="grid size-11 place-items-center rounded-borda text-texto-suave hover:bg-superficie-suave hover:text-texto active:bg-borda-suave"
          >
            <Icon nome="fechar" size={16} />
          </button>
        </div>
      </div>
      <div className="flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto px-4 pt-3.5 pb-5">
        {children}
      </div>
    </aside>
  );
}
